import { Anchor, Box, Menu, ResponsiveContext, Text, TextProps, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { useContext } from 'react';

interface IMenuItemProps {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  /** The page the user is on */
  active?: boolean;
}

interface IMenuProps {
  label: string;
  icon?: React.ReactNode;
  items: IMenuItemProps[];
  textColor?: TextProps['color'];
  disabled?: boolean;
  /** One of the menu's pages is the one the user is on */
  active?: boolean;
}

export const MenuWithChildren = ({ label, items, icon, textColor, disabled, active }: IMenuProps) => {
  const theme = useContext(ThemeContext);
  // In the header bar (not the phone drawer) the menu is as tall as the bar, so the active phase's
  // indicator sits on the bar's bottom edge
  const inBar = useContext(ResponsiveContext) !== 'small';

  const menuItems = items.map(({ label: itemLabel, active: itemActive, ...item }) => ({
    ...item,
    label: (
      <Text size="15px" color={itemActive ? 'brand' : 'text'} weight={itemActive ? 600 : undefined}>
        {itemLabel}
      </Text>
    ),
    'aria-current': itemActive ? ('page' as const) : undefined,
    style: { borderRadius: '8px', background: itemActive ? normalizeColor('brand-tint', theme) : undefined },
  }));

  return (
    <Menu
      plain
      items={menuItems}
      icon={icon}
      label={<Text color={textColor}>{label}</Text>}
      dropAlign={{ top: 'bottom', left: 'left' }}
      dropProps={{ a11yTitle: label, background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium' }}
      disabled={disabled}
    >
      <Anchor>
        <Box
          pad={inBar ? { horizontal: 'small' } : 'medium'}
          height={inBar ? '72px' : undefined}
          direction="row"
          gap="small"
          align="center"
          // The phase the user is in is underlined, the others dimmed
          style={
            active ? { boxShadow: `inset 0 -3px 0 ${normalizeColor(textColor ?? 'text', theme)}` } : { opacity: 0.85 }
          }
        >
          {/* Icons only in the phone drawer; the bar needs the room */}
          {!inBar && icon}
          <Text size="1rem" weight={active ? 600 : 500} color={textColor} style={{ whiteSpace: 'nowrap' }}>
            {' '}
            {label}
          </Text>
        </Box>
      </Anchor>
    </Menu>
  );
};
